import * as THREE from 'three';
import { t } from '../i18n';
import { numericFont, onAssetsReady, treatiseBodyFont } from '../ui/style';
import {
  HUD_CANVAS_H_PX,
  HUD_CANVAS_W_PX,
  HUD_OFFSET_X_M,
  HUD_OFFSET_Y_M,
  HUD_OFFSET_Z_M,
  HUD_PITCH_RAD,
  HUD_PLANE_HEIGHT_M,
  HUD_PLANE_WIDTH_M,
  UI_BAD,
  UI_GOOD,
  UI_INK,
  UI_INK_MUTED,
  UI_PAPER_DARK,
  UI_PAPER_LIGHT,
  UI_SEAL_RED,
} from '../config/constants';

type HudView = {
  timerLabel: string;
  lastShot: { zone: string; speed: number; edgeDeg: number } | null;
  /** Drill verdict for the last rep (replaces the zone line when present). */
  note?: { text: string; ok: boolean };
  /** Guard-mode readout: the held guardia name (or "fuori guardia"). Takes over the whole plane. */
  guard?: { text: string; recognized: boolean; debug?: string };
  /** Arcade-mode readout: live score + combo, shown under the countdown. */
  arcade?: { score: number; combo: number; multiplier: number };
};

/**
 * Wrist-mounted HUD: small printed-marginalia plane attached to the off-hand
 * controller. Too small for the full woodcut page border, so it uses a plain
 * paper ground with a thin engraved ink rule — EB Garamond labels, JetBrains
 * Mono kept for the timer/counters. Repaints every second during sessions, so
 * the ground stays a cheap gradient (no texture sampling).
 */
export class WristHud {
  readonly mesh: THREE.Mesh;
  private canvas: HTMLCanvasElement;
  private ctx2d: CanvasRenderingContext2D;
  private texture: THREE.CanvasTexture;
  private lastView: HudView = { timerLabel: '--:--', lastShot: null };
  private unsubscribe: (() => void) | null = null;
  /** The controller the HUD plane is currently parented to (owns its own mount). */
  private host: THREE.Object3D | null = null;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = HUD_CANVAS_W_PX;
    this.canvas.height = HUD_CANVAS_H_PX;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2d canvas context unavailable');
    this.ctx2d = ctx;
    this.texture = new THREE.CanvasTexture(this.canvas);

    const mat = new THREE.MeshBasicMaterial({ map: this.texture, transparent: true });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(HUD_PLANE_WIDTH_M, HUD_PLANE_HEIGHT_M), mat);
    this.mesh.rotation.x = HUD_PITCH_RAD;
    this.mesh.position.set(HUD_OFFSET_X_M, HUD_OFFSET_Y_M, HUD_OFFSET_Z_M);

    this.unsubscribe = onAssetsReady(() => this.render(this.lastView));
    this.render(this.lastView);
  }

  /**
   * Parent the HUD plane to a controller, moving it off its previous host if any.
   * Idempotent when the host is unchanged. The HUD owns its own mount so callers
   * don't track parents across controller swaps.
   */
  attachTo(controller: THREE.Object3D): void {
    if (this.host === controller) return;
    this.host?.remove(this.mesh);
    controller.add(this.mesh);
    this.host = controller;
  }

  render(view: HudView): void {
    this.lastView = view;
    const c = this.ctx2d;
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Paper ground (cheap gradient, no texture — repaints every second)
    const bg = c.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, UI_PAPER_LIGHT);
    bg.addColorStop(1, UI_PAPER_DARK);
    c.fillStyle = bg;
    c.fillRect(0, 0, w, h);

    // Thin engraved ink rule (double hairline) framing the marginalia
    const m = 12;
    const g = 4;
    c.strokeStyle = 'rgba(36,20,8,0.85)';
    c.lineWidth = 2;
    c.strokeRect(m, m, w - m * 2, h - m * 2);
    c.strokeStyle = 'rgba(36,20,8,0.5)';
    c.lineWidth = 1;
    c.strokeRect(m + g, m + g, w - (m + g) * 2, h - (m + g) * 2);

    // Guard mode takes over the plane: the held guardia, centered, auto-fit to the width.
    if (view.guard) {
      c.fillStyle = view.guard.recognized ? UI_INK : UI_INK_MUTED;
      const maxW = w - 56;
      let fontPx = h * 0.22;
      c.font = treatiseBodyFont(fontPx, 600);
      const textW = c.measureText(view.guard.text).width;
      if (textW > maxW) {
        fontPx *= maxW / textW;
        c.font = treatiseBodyFont(fontPx, 600);
      }
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillText(view.guard.text, w / 2, h * 0.48);
      if (view.guard.debug) {
        c.fillStyle = UI_INK_MUTED;
        c.font = numericFont(h * 0.11, 500);
        c.fillText(view.guard.debug, w / 2, h * 0.82);
      }
      this.texture.needsUpdate = true;
      return;
    }

    // Timer (monospace, tabular)
    c.fillStyle = UI_INK;
    c.textBaseline = 'alphabetic';
    c.textAlign = 'left';
    c.font = numericFont(h * 0.35, 700);
    c.fillText(view.timerLabel, 24, h * 0.46);

    // Arcade readout: score under the countdown, combo + multiplier on the bottom line.
    if (view.arcade) {
      c.fillStyle = UI_SEAL_RED;
      c.font = numericFont(h * 0.2, 700);
      c.textAlign = 'left';
      c.fillText(t('hudArcadeScore')(view.arcade.score), 24, h * 0.74);

      c.fillStyle = view.arcade.combo > 0 ? UI_GOOD : UI_INK_MUTED;
      c.font = treatiseBodyFont(h * 0.15, 600);
      c.fillText(t('hudArcadeCombo')(view.arcade.multiplier, view.arcade.combo), 24, h * 0.94);
      this.texture.needsUpdate = true;
      return;
    }

    // Verdict line (drill) takes the zone slot; otherwise show the zone label.
    if (view.note) {
      c.fillStyle = view.note.ok ? UI_GOOD : UI_BAD;
      c.font = treatiseBodyFont(h * 0.15, 600);
      c.textAlign = 'left';
      c.fillText(view.note.text, 24, h * 0.72);
    } else if (view.lastShot) {
      c.fillStyle = UI_SEAL_RED;
      c.font = treatiseBodyFont(h * 0.14, 600);
      c.textAlign = 'left';
      c.fillText(view.lastShot.zone, 24, h * 0.72);
    }

    if (view.lastShot) {
      // Speed + edge degrees (mono, ink)
      c.fillStyle = UI_INK;
      c.font = numericFont(h * 0.14, 500);
      c.textAlign = 'left';
      c.fillText(`${view.lastShot.speed.toFixed(1)} m/s`, 24, h * 0.92);

      c.fillStyle = UI_INK_MUTED;
      c.textAlign = 'right';
      c.fillText(`${view.lastShot.edgeDeg.toFixed(0)}°`, w - 24, h * 0.92);
    }

    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.host?.remove(this.mesh);
    this.host = null;
    this.texture.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.geometry.dispose();
  }
}
